import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Ship, Plane, Truck, Globe, TrendingUp, Shield, Users, MessageSquare, FileText, BarChart3, Menu, X, Monitor, ArrowRight, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [networkStats, setNetworkStats] = useState({ active_members: 0, countries: 0, quotes_value_processed: 0, total_quotes: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    const loadNetworkStats = async () => {
      try {
        const response = await api.get('/network/stats');
        setNetworkStats(response.data);
      } catch (error) {
        // Use default values if API fails
        setNetworkStats({ active_members: 4, countries: 2, quotes_value_processed: 0, total_quotes: 0 });
      }
    };
    loadNetworkStats();
  }, []);

  const formatValue = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M+`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K+`;
    if (value > 0) return `$${Math.round(value).toLocaleString()}`;
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-4 left-1/2 -translate-x-1/2 w-[90%] max-w-7xl bg-white/90 backdrop-blur-md border border-slate-200/50 rounded-full shadow-lg z-50 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="/logo-icon.png" 
              alt="CTC Logo" 
              className="h-10 w-10 object-contain rounded-lg"
            />
            <span className="text-xl font-outfit font-bold text-ocean-900">
              <span className="text-cyan-500">Connecting</span> The Caribbean
            </span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/#features" className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors">
              Features
            </Link>
            <Link to="/directory" className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors">
              Directory
            </Link>
            <Link to="/software" className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors">
              Software
            </Link>
            <Link to="/news" className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors">
              News
            </Link>
            <Link to="/#pricing" className="text-sm font-medium text-slate-600 hover:text-cyan-500 transition-colors">
              Pricing
            </Link>
            <Button 
              variant="outline"
              onClick={() => navigate('/login')} 
              className="border-2 border-ocean-900 text-ocean-900 hover:bg-ocean-900 hover:text-white font-semibold"
              data-testid="nav-signin-button"
            >
              <LogIn className="mr-2 h-4 w-4" />
              Sign In
            </Button>
            <Button 
              onClick={() => navigate('/register')} 
              className="bg-cyan-500 hover:bg-cyan-600 text-white shadow-glow font-semibold"
              data-testid="nav-join-button"
            >
              Join Network
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu - Redesigned */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-slate-200 animate-in slide-in-from-top duration-200">
            <div className="flex flex-col gap-1">
              <a href="/#features" className="flex items-center gap-3 text-base font-medium text-slate-700 py-3 px-4 rounded-lg hover:bg-slate-50 transition-colors">
                <TrendingUp className="h-5 w-5 text-cyan-500" />
                Features
              </a>
              <a href="/directory" className="flex items-center gap-3 text-base font-medium text-slate-700 py-3 px-4 rounded-lg hover:bg-slate-50 transition-colors">
                <Users className="h-5 w-5 text-cyan-500" />
                Directory
              </a>
              <a href="/software" className="flex items-center gap-3 text-base font-medium text-slate-700 py-3 px-4 rounded-lg hover:bg-slate-50 transition-colors">
                <Monitor className="h-5 w-5 text-cyan-500" />
                Software Marketplace
              </a>
              <a href="/news" className="flex items-center gap-3 text-base font-medium text-slate-700 py-3 px-4 rounded-lg hover:bg-slate-50 transition-colors">
                <FileText className="h-5 w-5 text-cyan-500" />
                News
              </a>
              <a href="/#pricing" className="flex items-center gap-3 text-base font-medium text-slate-700 py-3 px-4 rounded-lg hover:bg-slate-50 transition-colors">
                <BarChart3 className="h-5 w-5 text-cyan-500" />
                Pricing
              </a>
              
              <div className="border-t border-slate-200 mt-3 pt-4 space-y-3">
                <Button 
                  onClick={() => navigate('/login')} 
                  variant="outline"
                  className="w-full py-6 text-lg font-bold border-2 border-ocean-900 text-ocean-900 hover:bg-ocean-900 hover:text-white"
                  data-testid="mobile-signin-button"
                >
                  <LogIn className="mr-2 h-5 w-5" />
                  Sign In to Your Account
                </Button>
                <Button 
                  onClick={() => navigate('/register')} 
                  className="w-full py-6 text-lg font-bold bg-cyan-500 hover:bg-cyan-600 text-white shadow-glow"
                  data-testid="mobile-join-button"
                >
                  Join Network - Free First Year
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/1554646/pexels-photo-1554646.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-ocean-900/95 via-ocean-800/90 to-cyan-900/85" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Full Logo */}
            <div className="mb-8 flex justify-center">
              <img 
                src="/logo-full.png" 
                alt="Connecting The Caribbean - Freight Forwarders Network" 
                className="h-32 md:h-40 lg:h-48 object-contain drop-shadow-2xl"
              />
            </div>
            <p className="text-xl md:text-2xl text-slate-200 mb-8 max-w-3xl mx-auto">
              The premier B2B network for freight forwarders, NVOCCs, couriers, and logistics professionals across the Caribbean.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => navigate('/register')}
                className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-6 text-lg shadow-glow"
                data-testid="hero-get-started-button"
              >
                Get Started - Free First Year
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/directory')}
                className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20 px-8 py-6 text-lg"
                data-testid="hero-explore-directory-button"
              >
                Explore Directory
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 mt-16 max-w-3xl mx-auto">
              <div>
                <div className="text-4xl font-bold text-cyan-400">{networkStats.active_members || 0}+</div>
                <div className="text-slate-300 mt-1">Active Members</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-cyan-400">{networkStats.countries || 0}+</div>
                <div className="text-slate-300 mt-1">Countries</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-cyan-400">
                  {formatValue(networkStats.quotes_value_processed) || `${networkStats.total_quotes || 0}+`}
                </div>
                <div className="text-slate-300 mt-1">
                  {networkStats.quotes_value_processed > 0 ? 'Quotes Value' : 'Quotes Processed'}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-outfit font-bold text-ocean-900 mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              A comprehensive platform built specifically for Caribbean freight professionals
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: FileText,
                title: 'RFQ Marketplace',
                description: 'Post and respond to quotes seamlessly. Connect directly with verified members across the region.',
                color: 'cyan'
              },
              {
                icon: Shield,
                title: 'Verified Members Only',
                description: 'All members are vetted with country-specific documentation. Build trust through transparency.',
                color: 'green'
              },
              {
                icon: TrendingUp,
                title: 'Freight Insights',
                description: 'Access aggregated rate data by lane and mode. Make informed pricing decisions.',
                color: 'blue'
              },
              {
                icon: MessageSquare,
                title: 'Real-Time Messaging',
                description: 'Communicate instantly with partners. Share documents, negotiate terms, close deals faster.',
                color: 'purple'
              },
              {
                icon: Users,
                title: 'Member Directory',
                description: 'Discover partners by service type, location, and expertise. Expand your network strategically.',
                color: 'orange'
              },
              {
                icon: Monitor,
                title: 'Software Marketplace',
                description: 'Discover cargo software solutions. Browse TMS, WMS, and tracking tools with screenshots and demos.',
                color: 'indigo'
              }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="bg-white border border-slate-100 rounded-xl p-8 hover:shadow-float transition-all duration-300 group"
                data-testid={`feature-card-${idx}`}
              >
                <div className={`w-14 h-14 bg-${feature.color}-100 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className={`h-7 w-7 text-${feature.color}-600`} />
                </div>
                <h3 className="text-xl font-outfit font-bold text-ocean-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-outfit font-bold text-ocean-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-slate-600">Join the network in three simple steps</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {[
              { step: '01', title: 'Register & Verify', description: 'Create your company profile and upload required documents for your country.' },
              { step: '02', title: 'Get Approved', description: 'Our team reviews your application. Most approvals happen within 48 hours.' },
              { step: '03', title: 'Start Connecting', description: 'Access the marketplace, post RFQs, send quotes, and grow your business.' }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                className="text-center"
              >
                <div className="text-6xl font-outfit font-bold text-cyan-500/20 mb-4">{item.step}</div>
                <h3 className="text-2xl font-outfit font-bold text-ocean-900 mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-outfit font-bold text-ocean-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-slate-600">Start with a full year free. No credit card required.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="border-2 border-slate-200 rounded-2xl p-8"
            >
              <h3 className="text-2xl font-outfit font-bold text-ocean-900 mb-2">Trial</h3>
              <div className="mb-6">
                <span className="text-5xl font-bold text-ocean-900">$0</span>
                <span className="text-slate-600">/year</span>
              </div>
              <p className="text-slate-600 mb-6">First year completely free for all new members</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-slate-600">Full platform access</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-slate-600">Unlimited RFQs & quotes</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-slate-600">Freight rate insights</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="border-2 border-cyan-500 rounded-2xl p-8 relative shadow-glow"
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-cyan-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                Most Popular
              </div>
              <h3 className="text-2xl font-outfit font-bold text-ocean-900 mb-2">Monthly</h3>
              <div className="mb-6">
                <span className="text-5xl font-bold text-ocean-900">$99.99</span>
                <span className="text-slate-600">/month</span>
              </div>
              <p className="text-slate-600 mb-6">After your first year</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-slate-600">Everything in Trial</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-slate-600">Priority support</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-slate-600">Cancel anytime</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="border-2 border-slate-200 rounded-2xl p-8"
            >
              <h3 className="text-2xl font-outfit font-bold text-ocean-900 mb-2">Yearly</h3>
              <div className="mb-6">
                <span className="text-5xl font-bold text-ocean-900">$699</span>
                <span className="text-slate-600">/year</span>
              </div>
              <p className="text-slate-600 mb-6">Save $500 annually</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-slate-600">Everything in Monthly</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-slate-600">Best value</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-slate-600">One simple payment</span>
                </li>
              </ul>
            </motion.div>
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg"
              onClick={() => navigate('/register')}
              className="bg-ocean-900 hover:bg-ocean-800 text-white px-10 py-6 text-lg"
              data-testid="pricing-join-button"
            >
              Start Your Free Year Today
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-ocean-900 via-ocean-800 to-cyan-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-outfit font-bold mb-6">
            Ready to Connect the Caribbean?
          </h2>
          <p className="text-xl text-slate-200 mb-8">
            Join hundreds of freight professionals building stronger connections across the region.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-cyan-500 hover:bg-cyan-600 text-white px-10 py-6 text-lg shadow-glow"
            data-testid="cta-join-button"
          >
            Get Started Now - Free for 1 Year
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-ocean-900 text-slate-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Ship className="h-6 w-6 text-cyan-500" />
                <span className="font-outfit font-bold text-white">CTC Cargo Network</span>
              </div>
              <p className="text-sm">Connecting the Caribbean freight industry</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Platform</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/directory" className="hover:text-cyan-400">Directory</Link></li>
                <li><Link to="/software" className="hover:text-cyan-400">Software Marketplace</Link></li>
                <li><Link to="/#features" className="hover:text-cyan-400">Features</Link></li>
                <li><Link to="/#pricing" className="hover:text-cyan-400">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/news" className="hover:text-cyan-400">News</Link></li>
                <li><Link to="/events" className="hover:text-cyan-400">Events</Link></li>
                <li><a href="#" className="hover:text-cyan-400">Learning Hub</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-cyan-400">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-cyan-400">Terms of Service</a></li>
                <li><a href="#" className="hover:text-cyan-400">Contact Us</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-700 pt-8 text-center text-sm">
            <p>&copy; 2025 CTC Cargo Network. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
